import type { AssetClass, ProductSelectionItem } from "@repo/shared/types";
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useReorderSelection, useSelections } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./selections-dialog.i18n";

type SelectionsDialogProps = {
  open: boolean;
  portfolioCollectionId: string;
  assetClass: AssetClass | null;
  onClose: () => void;
};

export function SelectionsDialog({
  open,
  portfolioCollectionId,
  assetClass,
  onClose,
}: SelectionsDialogProps) {
  const localize = useLocalize();
  const t = useT(T);
  const [reorderError, setReorderError] = useState<string | null>(null);

  const { selections, isLoading } = useSelections(
    open && assetClass ? portfolioCollectionId : null,
    open && assetClass ? assetClass.id : null,
  );
  const reorderSelection = useReorderSelection();

  const handleClose = () => {
    setReorderError(null);
    onClose();
  };

  const handleMove = (selection: ProductSelectionItem, direction: -1 | 1) => {
    if (!assetClass) return;
    reorderSelection.mutate(
      {
        collectionId: portfolioCollectionId,
        assetClassId: assetClass.id,
        productId: selection.productId,
        newRank: selection.rank + direction,
      },
      {
        onSuccess: () => setReorderError(null),
        onError: (err: Error) => setReorderError(err.message || t.failedToReorder),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t.title}
            {assetClass ? ` — ${localize(assetClass.nameI18n)}` : ""}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : selections.length === 0 ? (
          <EmptyState>{t.noSelections}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">{t.rank}</TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.isin}</TableHead>
                <TableHead>{t.status}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selections.map((selection, index) => (
                <TableRow key={selection.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      <span className="w-6">{selection.rank}</span>
                      <Button
                        size="icon"
                        tooltip={t.moveUp}
                        variant="ghost"
                        onClick={() => handleMove(selection, -1)}
                        disabled={reorderSelection.isPending || index === 0}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.moveDown}
                        variant="ghost"
                        onClick={() => handleMove(selection, 1)}
                        disabled={reorderSelection.isPending || index === selections.length - 1}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell
                    className={selection.isActive ? "font-medium" : "text-muted-foreground"}
                  >
                    {localize(selection.nameI18n)}
                  </TableCell>
                  <TableCell>
                    {selection.isin ? (
                      <code className="text-xs bg-muted px-2 py-1 rounded">{selection.isin}</code>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {selection.isActive ? (
                      <Badge variant="success">{t.active}</Badge>
                    ) : (
                      <Badge variant="secondary">{t.inactive}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {reorderError && <p className="text-sm text-destructive">{reorderError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
